/**
 * TopMovers - Dashboard card showing top gainers and losers
 * 
 * Displays the positions with the largest price movements over the selected period.
 */

import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw } from 'lucide-react';
import Sparkline from './Sparkline';
import { fetchApi } from '../api/client';

function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function TopMovers({ limit, onViewAll } = {}) {
  const [data, setData] = useState({ gainers: [], losers: [] });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [error, setError] = useState(null);

  const fetchMovers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApi(`/velocity/movers/${period}?n=10`);
      setData(result);
    } catch (err) {
      console.error('Error fetching movers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovers();
  }, [period]);

  const MoverRow = ({ item, isGainer }) => {
    const returnValue = item.returns?.[period] || 0;
    const colorClass = isGainer ? 'text-emerald-400' : 'text-red-400';
    
    return (
      <div className="flex items-center gap-3 py-2 border-b border-slate-700/50 last:border-0">
        <div className="w-16 font-medium text-slate-200">{item.ticker}</div>
        <div className="flex-1">
          <Sparkline 
            data={item.sparkline || []} 
            width={80} 
            height={24}
            strokeWidth={1.5}
          />
        </div>
        <div className="w-20 text-right font-mono text-sm text-slate-400">
          {formatCurrency(item.current_price)}
        </div>
        <div className={`w-16 text-right font-mono font-medium ${colorClass}`}>
          {returnValue > 0 ? '+' : ''}{returnValue.toFixed(1)}%
        </div>
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-400" />
          Top Movers
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
          >
            <option value="7d">7 Day</option>
            <option value="14d">14 Day</option>
            <option value="30d">30 Day</option>
            <option value="90d">90 Day</option>
          </select>
          <button
            onClick={fetchMovers}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-red-400 text-sm mb-4">Error: {error}</div>
      )}

      {loading && !data.gainers.length ? (
        <div className="flex justify-center py-8">
          <div className="spinner w-6 h-6"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Gainers */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-emerald-400">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-medium">Top Gainers</span>
            </div>
            {data.gainers.length > 0 ? (
              <div className="space-y-0">
                {(limit ? data.gainers.slice(0, limit) : data.gainers).map((item) => (
                  <MoverRow key={item.ticker} item={item} isGainer={true} />
                ))}
              </div>
            ) : (
              <div className="text-slate-500 text-sm py-4 text-center">
                No gainers in this period
              </div>
            )}
          </div>

          {/* Losers */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-red-400">
              <TrendingDown className="w-4 h-4" />
              <span className="text-sm font-medium">Top Losers</span>
            </div>
            {data.losers.length > 0 ? (
              <div className="space-y-0">
                {(limit ? data.losers.slice(0, limit) : data.losers).map((item) => (
                  <MoverRow key={item.ticker} item={item} isGainer={false} />
                ))}
              </div>
            ) : (
              <div className="text-slate-500 text-sm py-4 text-center">
                No losers in this period
              </div>
            )}
          </div>
        </div>
      )}

      {limit && onViewAll && (data.gainers.length > limit || data.losers.length > limit) ? (
        <div className="mt-4 pt-3 border-t border-slate-700 text-center">
          <button
            onClick={onViewAll}
            className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
          >
            View all movers &rarr;
          </button>
        </div>
      ) : data.total_positions > 0 ? (
        <div className="mt-4 pt-3 border-t border-slate-700 text-xs text-slate-500 text-center">
          Tracking {data.total_positions} positions
        </div>
      ) : null}
    </div>
  );
}
